import { useState, useEffect, useCallback, useRef } from "react";
import ReactDataSheet from "react-datasheet";
import "react-datasheet/lib/react-datasheet.css";
import "./TariffDataSheet.css";
import { periodColors } from "./TarifUtils";
import { SeChip } from "@se/web-ui-react";
import { uniqueId } from "lodash";
import { useMantineColorScheme, Menu, Select } from "@mantine/core";
import { useCurrency } from "../utils/helpers";
import PropTypes from "prop-types";
import { IconDotsVertical } from "@tabler/icons-react";
import { useDualTranslation } from "../utils/i18nHelpers";

const TariffDataSheet = ({
  activeDayGroup,
  updateTier,
  updateTimeSlot,
  callSnackbar,
  updatePeriodName,
  handleRemoveTimeSlots,
  currentDate,
  updateTierFractionInDayGroup,
  updateSelectedSeasonAndDayGroup,
  allowBill,
  allowPenalty,
  isReadOnly,
}) => {
  const { t } = useDualTranslation();
  const currency = useCurrency();
  const [grid, setGrid] = useState([]);
  const [selectedTime, setSelectedTime] = useState([]);
  const [dynamicClass, setDynamicClass] = useState("data-grid-100");
  const { colorScheme } = useMantineColorScheme();
  const [headerTierMenu, setHeaderTierMenu] = useState({
    opened: false,
    tierId: null,
    anchor: null,
  });

  // Visibility now controlled by boolean flags stored on each tier: fixedRequired, fractionRequired

  // Add a ref for the horizontal scroller
  const scrollerRef = useRef(null);
  const prevTierCountRef = useRef(null);

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const formattedHour = hour.toString().padStart(2, "0");
        const formattedMinute = minute.toString().padStart(2, "0");
        options.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    options.push("24:00");
    return options;
  };

  

  const timeOptions = generateTimeOptions();
   let filteredTimeOptions = timeOptions;
  const is24Used = activeDayGroup.rate.some((tariff) =>
    tariff.timeSlots.some((slot) =>
      slot.endTime.hour === 24 && slot.endTime.minute === 0
    )
  );
  if (is24Used) {
    filteredTimeOptions = timeOptions.filter((t) => t !== "24:00");
  }

  useEffect(() => {
    // Re-render grid when structural inputs or enabledExtras change
    initalRenderer();
  }, [activeDayGroup, activeDayGroup?.rate, selectedTime, currentDate]);

  useEffect(() => {
    const tableCells = document.querySelectorAll(".data-grid td");
    if (colorScheme === "dark") {
      tableCells.forEach((cell) => {
        cell.classList.add("dark-col");
      });
    } else {
      tableCells.forEach((cell) => {
        cell.classList.remove("dark-col");
      });
    }
  }, [colorScheme]);

  const calculateClassName = (tierCount) => {
    if (tierCount <= 2) return "data-grid-100";
    const raw = 100 + (tierCount - 2) * 25; // original progression
    // Clamp to maximum defined class
    if (raw >= 1000) return "data-grid-1000";
    return `data-grid-${raw}`;
  };

  const handleRemoveTimeSlot = (index, tariffId) => {
    if (isReadOnly) return;
    handleRemoveTimeSlots(index, tariffId);
    setSelectedTime([])
    initalRenderer();
  };
    const disabledTimes = new Set();
  const allSlotEndTimes = new Set();
  const allSlotStartTimes = new Set();
  activeDayGroup.rate.forEach((tariff) => {
    tariff.timeSlots.forEach((slot) => {
      let startMinutes = slot.startTime.hour * 60 + slot.startTime.minute;
      let endMinutes = slot.endTime.hour * 60 + slot.endTime.minute;
      allSlotEndTimes.add(`${slot.endTime.hour.toString().padStart(2, "0")}:${slot.endTime.minute.toString().padStart(2, "0")}`);
      allSlotStartTimes.add(`${slot.startTime.hour.toString().padStart(2, "0")}:${slot.startTime.minute.toString().padStart(2, "0")}`);
      // Only disable times strictly inside the slot (exclude start and end)
      for (let min = startMinutes + 15; min < endMinutes; min += 15) {
        const hour = Math.floor(min / 60)
          .toString()
          .padStart(2, "0");
        const minute = (min % 60).toString().padStart(2, "0");
        disabledTimes.add(`${hour}:${minute}`);
      }
    });
  });

  const initalRenderer = useCallback(() => {
  const tierCount = activeDayGroup.rate[0].options.tierBoundaries.length;
  const calculatedClass = calculateClassName(tierCount);
  setDynamicClass(calculatedClass);

    const headers = [
      {
        value: "",
        readOnly: true,
        width: "10%",
        className: ` sticky-col1 ${
          colorScheme === "dark" ? "dark-col" : ""
        }`,
      },
      {
        value: t("tariff.dataSheet.duration"),
        colSpan: 2,
        className: `cell-with-comment sticky-col2 ${
          colorScheme === "dark" ? "dark-col" : ""
        }`,
        readOnly: true,
        disableEvents: true,
        width: "18%",
        valueViewer: (props) => (
          <>
            <span className="font-header-cell">{props.value}</span>
          </>
        ),
      },
      ...activeDayGroup.rate[0].options.tierBoundaries.map((tier, index) => {
        const showFraction = !!tier.fractionRequired;
        const isOnlyTier = tierCount === 1;
        const isLastTier = index === tierCount - 1;
        // Hide consumption ALWAYS for last tier (even if only one tier)
        const hideConsumption = isLastTier;
        const baseCols = hideConsumption ? 1 : 2;
        const colSpan = baseCols + (showFraction ? 1 : 0);
        return {
          value: (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>
                {isOnlyTier
                  ? t("tariff.dataSheet.charges")
                    : t("tariff.dataSheet.tierLabel", { index: index + 1 })}
              </span>
              <Menu
                position="right-start"
                withArrow
                withinPortal
                shadow="md"
                offset={2}
                styles={{
                  dropdown: {
                    backgroundColor: colorScheme === "dark" ? "#23272e" : "#fff",
                    color: colorScheme === "dark" ? "#fff" : "#000",
                    border: colorScheme === "dark" ? "1px solid #333" : undefined,
                  },
                  item: { color: colorScheme === "dark" ? "#fff" : "#000" },
                }}
              >
                <Menu.Target>
                  <span
                    style={{
                      marginLeft: 6,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    }}
                    title={t("tariff.dataSheet.more")}
                  >
                    <IconDotsVertical size={16} />
                  </span>
                </Menu.Target>
                <Menu.Dropdown>
                  <div className="p-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={!!tier.fractionRequired}
                        onChange={(e) => handleCheckChange(index, "fraction", e.target.checked)}
                      />
                      <label className="form-check-label">
                        {t("tariff.dataSheet.fraction")}
                      </label>
                    </div>
                    {allowPenalty && (
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={tier.hasOwnProperty("penalty")}
                          onChange={(e) => handleCheckChange(index, "penalty", e.target.checked)}
                        />
                        <label className="form-check-label">
                          {t("tariff.dataSheet.penalty")}
                        </label>
                      </div>
                    )}
                    {allowBill && (
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={tier.hasOwnProperty("minimumBilling")}
                          onChange={(e) => handleCheckChange(index, "minimumBilling", e.target.checked)}
                        />
                        <label className="form-check-label">
                          {t("tariff.dataSheet.minimumBilling")}
                        </label>
                      </div>
                    )}
                  </div>
                </Menu.Dropdown>
              </Menu>
            </div>
          ),
          tierId: tier.tierId,
          colSpan: colSpan,
          className: `cell-with-comment ${colorScheme === "dark" ? "dark-col" : ""}`,
          readOnly: true,
          disableEvents: false,
          type: "tier",
          width: "30%",
          style: { backgroundColor: "red" },
        };
      }),
    ];
    const subHeaders = [
      {
        value: t("tariff.dataSheet.timePeriods"),
        readOnly: true,
        className: `cell-with-comment sticky-col1 ${
          colorScheme === "dark" ? "dark-col" : ""
        }`,
        disableEvents: true,
        valueViewer: (props) => (
          <>
            <span className="font-cell">{props.value}</span>
          </>
        ),
      },
      {
        value: t("tariff.dataSheet.from"),
        className: `cell-with-comment sticky-col2 ${
          colorScheme === "dark" ? "dark-col" : ""
        }`,
        readOnly: true,
        disableEvents: true,
        valueViewer: (props) => (
          <>
            <span className="font-cell">{props.value}</span>
          </>
        ),
      },
      {
        value: t("tariff.dataSheet.to"),
        className: `cell-with-comment sticky-col3 ${
          colorScheme === "dark" ? "dark-col" : ""
        }`,
        readOnly: true,
        disableEvents: true,
        valueViewer: (props) => (
          <>
            <span className="font-cell">{props.value}</span>
          </>
        ),
      },

      ...activeDayGroup.rate[0].options.tierBoundaries.flatMap((tier, index) => {
        const isOnlyTier = tierCount === 1;
        const isLastTier = index === tierCount - 1;
        const hideConsumption = isLastTier; // always hide last tier consumption
        const cells = [
          {
            value: t("tariff.dataSheet.energyCharges", { currency }),
            className: `cell-with-comment  ${colorScheme === "dark" ? "dark-col" : ""}`,
            readOnly: true,
            disableEvents: true,
            valueViewer: (props) => (
              <>
                <span className="font-cell">{props.value}</span>
              </>
            ),
          },
          // Hide Consumption header always for last tier
          ...(!hideConsumption
            ? [
                {
                  value: t("tariff.dataSheet.consumption"),
                  className: `cell-with-comment  ${colorScheme === "dark" ? "dark-col" : ""}`,
                  readOnly: true,
                  disableEvents: true,
                  width: "20%",
                  valueViewer: (props) => (
                    <>
                      <span className="font-cell" style={{ wordWrap: "break-word" }}>
                        {props.value}
                      </span>
                    </>
                  ),
                },
              ]
            : []),
        ];
        if (tier.fractionRequired) {
          cells.push({
            value: t("tariff.dataSheet.fractionCurrency", { currency }),
            className: `cell-with-comment  ${colorScheme === "dark" ? "dark-col" : ""}`,
            readOnly: true,
            disableEvents: true,
            valueViewer: (props) => (
              <>
                <span className="font-cell">{props.value}</span>
              </>
            ),
          });
        }
        return cells;
      }),
    ];
    const rows = activeDayGroup.rate.map((tariff, rowIndex) => [
      {
        value: tariff.name,
        type: "tariffName",
        className: `background-transparent sticky-col1 ${
          colorScheme === "dark" ? "dark-col" : ""
        }`,
        valueViewer: (props) => (
          <>
            <span className="font-cell period-name">{props.value}</span>
            <span
              className="label-circle"
              style={{ background: `${periodColors[rowIndex]}` }}
            ></span>
          </>
        ),
      },
      {
        value: (
          <div>
            <div className="time-select-container">
              <Select
                key={uniqueId()}
                data={filteredTimeOptions.map((option) => ({
                  value: option,
                  label: option,
                  disabled:
                    disabledTimes.has(option) || allSlotStartTimes.has(option),
                }))}
                value={selectedTime[rowIndex]?.startTime || timeOptions[0]}
                onChange={(value) => handleTimeChange(rowIndex, "startTime", value)}
                size="xs"
                style={{ width: 90, marginRight: 8 }}
                placeholder={t("tariff.dataSheet.start")}
                searchable={true}
                nothingFound="No options"
                withinPortal
                disabled={isReadOnly}
              />
              <Select
                key={uniqueId()}
                data={filteredTimeOptions.filter(option => !allSlotEndTimes.has(option)).map((option) => ({
                  value: option,
                  label: option,
                  // Disable if in disabledTimes, or if option is 00:00 (hour 0)
                  disabled: disabledTimes.has(option) || option === "00:00",
                }))}
                value={selectedTime[rowIndex]?.endTime || timeOptions[0]}
                onChange={(value) => handleTimeChange(rowIndex, "endTime", value)}
                size="xs"
                style={{ width: 90, marginRight: 8 }}
                placeholder={t("tariff.dataSheet.end")}
                searchable={true}
                nothingFound="No options"
                withinPortal
                disabled={isReadOnly}
              />
              <se-button
                color="standard"
                style={{ width: "50px" }}
                aria-label={`add-timeslot-row-${rowIndex}`}
                onClick={() => handleAddTimeSlot(rowIndex)}
                disabled={isReadOnly}
              >
                {t("tariff.dataSheet.add")}
              </se-button>
            </div>
            <div className="chip">
              {tariff.timeSlots.length > 0 &&
                tariff.timeSlots.map((slot, index) => (
                  <SeChip
                    size="small"
                    key={index}
                    aria-label={`tariff-timeslot-chip-row-${rowIndex}-index-${index}`}
                    value={`${slot.startTime.hour}:${slot.startTime.minute} - ${slot.endTime.hour}:${slot.endTime.minute}`}
                    can-close={!isReadOnly}
                    onDidClose={() =>
                      !isReadOnly && handleRemoveTimeSlot(index, tariff.tariffId)
                    }
                  ></SeChip>
                ))}
            </div>
          </div>
        ),
        colSpan: 2,
        readOnly: true,
        disableEvents: true,
        className: `sticky-col2 ${colorScheme === "dark" ? "dark-col" : ""}`,
      },
      ...tariff.options.tierBoundaries.flatMap((tier, index) => {
        const isOnlyTier = tierCount === 1;
        const isLastTier = index === tierCount - 1;
        const hideConsumption = isLastTier; // always hide last tier consumption
        const cells = [
          {
            value: tier.price,
            tariffId: tariff.tariffId,
            tierId: tier.tierId,
            type: "price",
            readOnly: false,
          },
          // Hide Consumption value cell always for last tier
          ...(!hideConsumption
            ? [
                {
                  value: tier.consumption,
                  tariffId: tariff.tariffId,
                  tierId: tier.tierId,
                  type: "consumption",
                  readOnly: false,
                },
              ]
            : []),
        ];
        if (tier.fractionRequired) {
          cells.push({
            value: tier.fraction,
            tariffId: tariff.tariffId,
            tierId: tier.tierId,
            type: "fraction",
            readOnly: false,
          });
        }
        return cells;
      }),
    ]);
    setGrid([headers, subHeaders, ...rows]);
    // Only auto-scroll right when a new tier column is added
    const currentTierCount = activeDayGroup?.rate?.[0]?.options?.tierBoundaries?.length ?? 0;
    if (prevTierCountRef.current !== null && currentTierCount > prevTierCountRef.current) {
      setTimeout(() => {
        if (scrollerRef.current) {
          scrollerRef.current.scrollLeft = scrollerRef.current.scrollWidth;
        }
      }, 0);
    }
    prevTierCountRef.current = currentTierCount;
  }, [
    activeDayGroup,
    selectedTime,
    currentDate,
    colorScheme,
    allowBill,
    allowPenalty,
    // requirement flags embedded in tier objects trigger re-render through activeDayGroup changes
  ]);

  const handleAddTimeSlot = (periodIndex) => {
    if (isReadOnly) return;
    const startTime = selectedTime[periodIndex]?.startTime || timeOptions[0];
    const endTime = selectedTime[periodIndex]?.endTime || timeOptions[0];
    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    const newStartTime = { hour: startHour, minute: startMinute };
    const newEndTime = { hour: endHour, minute: endMinute };

    // Validate if the new time slot is in reverse order
    if (
      newStartTime.hour > newEndTime.hour ||
      (newStartTime.hour === newEndTime.hour &&
        newStartTime.minute >= newEndTime.minute)
    ) {
      callSnackbar("error", "start / end time are incorrect");

      return;
    }

    // Validate if the new time slot overlaps with any existing time slots
    const isOverlapping = activeDayGroup.rate.some((tariff) =>
      tariff.timeSlots.some(
        (slot) =>
          (newStartTime.hour < slot.endTime.hour ||
            (newStartTime.hour === slot.endTime.hour &&
              newStartTime.minute < slot.endTime.minute)) &&
          (newEndTime.hour > slot.startTime.hour ||
            (newEndTime.hour === slot.startTime.hour &&
              newEndTime.minute > slot.startTime.minute))
      )
    );

    if (isOverlapping) {
      callSnackbar("error", "Time slots overlapped");
      return;
    }
    updateTimeSlot(periodIndex, newStartTime, newEndTime);
    setSelectedTime([], (prevState) => {
      initalRenderer();
    });
  };

  const handleTimeChange = (periodIndex, key, value) => {
    if (isReadOnly) return;
    setSelectedTime((prevState) => ({
      ...prevState,
      [periodIndex]: {
        ...prevState[periodIndex],
        [key]: value,
      },
    }));
  };
  // Fix: Clear all selected cells, not just the first match per row/col
  const handleCellsChanged = (changes) => {
    if (isReadOnly) return;
    // Always update the parent state first, then update the grid
    // Clone the current day group to batch all changes
    const updatedDayGroup = JSON.parse(JSON.stringify(activeDayGroup));
    let updateCell = true;

    changes.forEach(({ cell, row, col, value }) => {
      if (value === undefined) {
        updateCell = false;
        return;
      }
      if (row > 1) {
        const periodIndex = row - 2;
        const tariff = updatedDayGroup.rate[periodIndex];
        const tierIndex = tariff.options.tierBoundaries.findIndex(
          (a) => a.tierId === cell.tierId
        );
        const tier = tariff.options.tierBoundaries[tierIndex];

        if (cell.type === "tariffName") {
          tariff.name = value;
          updatePeriodName(periodIndex, value);
        } else if (["price", "consumption", "fixed", "fraction", "penalty", "minimumBilling"].includes(cell.type)) {
          tier[cell.type] = value;
        }
      }
    });

    // Batch update parent state first, so grid will always reflect latest data
    if (updateCell && typeof updateTierFractionInDayGroup === "function") {
      updatedDayGroup.rate.forEach((tariff, periodIndex) => {
        tariff.options.tierBoundaries.forEach((tier, tierIndex) => {
          updateTierFractionInDayGroup(
            periodIndex,
            tierIndex,
            tier.price,
            tier.consumption,
            tier.fixed,
            tier.fraction,
            tier.penalty,
            tier.minimumBilling // <-- changed from billing to minimumBilling
          );
        });
      });
    }

    // Now update the grid to reflect the changes
    if (updateCell) {
      const newGrid = grid.map((row) => [...row]);
      changes.forEach(({ cell, row, col, value }) => {
        if (row > 1) {
          newGrid[row][col] = { ...newGrid[row][col], value };
        }
      });
      setGrid(newGrid);
    }
  };

  const [selectedTier, setSelectedTier] = useState(null);

  const handleCheckChange = (tierIndex, key, checked) => {
    if (isReadOnly) return;
    // Map UI keys fixed/fraction to required flags
    let requiredKey = key;
    if (key === 'fixed') requiredKey = 'fixedRequired';
    if (key === 'fraction') requiredKey = 'fractionRequired';
    updateSelectedSeasonAndDayGroup(tierIndex, requiredKey, checked);
  };

  const GlobalNumberEditor = (props) => {
    const inputRef = useRef();
    const [localValue, setLocalValue] = useState(props?.cell?.value || "");

    useEffect(() => {
      setLocalValue(props?.cell?.value || "");
    }, [props.cell.value]);

    // Always call onChange with the latest value before onCommit
    const commitValue = () => {
      // Always propagate the current localValue (including empty string)
      if (props.onChange) props.onChange(localValue);
      if (props.onCommit) props.onCommit(localValue);
    };

    return (
      <input
        type="text"
        ref={inputRef}
        value={localValue}
        onChange={(e) => {
          const val = e.target.value;
          const MAX_LEN = 64; // defensive cap
          // Relaxed numeric pattern that allows intermediate states like '', '-', '.', '1.' etc.
          const relaxedNumberRegex = /^-?(?:\d*(?:\.\d*)?)$/;
          if (
            (val.length <= MAX_LEN && (val === "" || relaxedNumberRegex.test(val))) ||
            props?.cell?.type === "tariffName"
          ) {
            setLocalValue(val);
            if (props.onChange) props.onChange(val);
          }
        }}
        onBlur={commitValue}
        onKeyDown={(e) => {
          if (e.key === "Tab" || e.key === "Enter") {
            e.preventDefault();
            commitValue();
          }
        }}
        style={{
          width: "100%",
          border: "none",
          height: "100%",
          textAlign: "center",
        }}
        autoFocus
      />
    );
  };

  return (
    <div className="data-sheet-container pl-2">
      <div className="zui-scroller" ref={scrollerRef}>
        <ReactDataSheet
          data={grid}
          valueRenderer={(cell) => cell.value}
          dataRenderer={(cell) => cell.value}
          onCellsChanged={isReadOnly ? () => {} : handleCellsChanged}
          className={dynamicClass}
          dataEditor={GlobalNumberEditor}
        />
        <br />
        {/* ...existing context menu for data cells if needed... */}
      </div>
    </div>
  );
};

TariffDataSheet.propTypes = {
  activeDayGroup: PropTypes.object,
  updateTier: PropTypes.func,
  updateTimeSlot: PropTypes.func,
  callSnackbar: PropTypes.func,
  updatePeriodName: PropTypes.func,
  handleRemoveTimeSlots: PropTypes.func,
  currentDate: PropTypes.instanceOf(Date),
  updateTierFractionInDayGroup: PropTypes.func,
  updateSelectedSeasonAndDayGroup: PropTypes.func,
  allowBill: PropTypes.bool,
  allowPenalty: PropTypes.bool,
  isReadOnly: PropTypes.bool,
};

export default TariffDataSheet;
